import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as SkillIcon,
  Close as CloseIcon,
  ContentCopy as CloneIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Public as PublicIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentApi } from '../../services/api';
import type {
  AgentSkill,
  AgentSkillCreate,
  AgentSkillUpdate,
} from '../../types';

type SkillFormState = {
  name: string;
  description: string;
  category: string;
  tags: string;
  instructions: string;
  triggerDescription: string;
  triggerKeywords: string;
  requiredTools: string;
  optionalTools: string;
  resources: string;
  configSchema: string;
  tests: string;
  isActive: boolean;
  isPublic: boolean;
};

const EMPTY_FORM: SkillFormState = {
  name: '',
  description: '',
  category: '',
  tags: '',
  instructions: '',
  triggerDescription: '',
  triggerKeywords: '',
  requiredTools: '',
  optionalTools: '',
  resources: '[]',
  configSchema: '{}',
  tests: '[]',
  isActive: true,
  isPublic: false,
};

const csv = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const jsonValue = <T,>(value: string, fallback: T): T => {
  const trimmed = value.trim();
  return trimmed ? (JSON.parse(trimmed) as T) : fallback;
};

const firstErrorMessage = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
  }
  return null;
};

const errorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  return firstErrorMessage(data?.detail) || firstErrorMessage(data?.error) ||
    firstErrorMessage(data) || fallback;
};

const formFromSkill = (skill: AgentSkill): SkillFormState => {
  const version = skill.current_version;
  return {
    name: skill.name,
    description: skill.description || '',
    category: skill.category || '',
    tags: (skill.tags || []).join(', '),
    instructions: version?.instructions || '',
    triggerDescription: version?.trigger_description || '',
    triggerKeywords: (version?.trigger_keywords || []).join(', '),
    requiredTools: (version?.required_tools || []).join(', '),
    optionalTools: (version?.optional_tools || []).join(', '),
    resources: JSON.stringify(version?.resources || [], null, 2),
    configSchema: JSON.stringify(version?.config_schema || {}, null, 2),
    tests: JSON.stringify(version?.tests || [], null, 2),
    isActive: skill.is_active,
    isPublic: skill.is_public,
  };
};

const VERSION_FORM_FIELDS: Array<keyof SkillFormState> = [
  'instructions',
  'triggerDescription',
  'triggerKeywords',
  'requiredTools',
  'optionalTools',
  'resources',
  'configSchema',
  'tests',
];

const SkillManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<AgentSkill | null>(null);
  const [form, setForm] = useState<SkillFormState>(EMPTY_FORM);
  const [deleteSkill, setDeleteSkill] = useState<AgentSkill | null>(null);

  const mineQuery = useQuery({
    queryKey: ['agent-skills', 'mine'],
    queryFn: () => agentApi.listSkills({ scope: 'mine' }),
  });
  const publicQuery = useQuery({
    queryKey: ['agent-skills', 'public'],
    queryFn: () => agentApi.listSkills({ scope: 'public', is_public: true }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['agent-skills'] });
  };

  const saveMutation = useMutation({
    mutationFn: ({
      skill,
      payload,
    }: {
      skill: AgentSkill | null;
      payload: AgentSkillCreate | AgentSkillUpdate;
    }) =>
      skill
        ? agentApi.updateSkill(skill.id, payload as AgentSkillUpdate)
        : agentApi.createSkill(payload as AgentSkillCreate),
    onSuccess: (_saved, variables) => {
      invalidate();
      setDialogOpen(false);
      setEditingSkill(null);
      setForm(EMPTY_FORM);
      toast.success(variables.skill ? 'Skill saved' : 'Skill created');
    },
    onError: (error: any) =>
      toast.error(errorMessage(error, 'Failed to save skill')),
  });

  const cloneMutation = useMutation({
    mutationFn: (skill: AgentSkill) => agentApi.cloneSkill(skill.id),
    onSuccess: () => {
      invalidate();
      setTab(0);
      toast.success('Private skill created from template');
    },
    onError: (error: any) =>
      toast.error(errorMessage(error, 'Failed to create skill from template')),
  });

  const deleteMutation = useMutation({
    mutationFn: (skill: AgentSkill) => agentApi.deleteSkill(skill.id),
    onSuccess: () => {
      invalidate();
      setDeleteSkill(null);
      toast.success('Skill deleted');
    },
    onError: (error: any) =>
      toast.error(errorMessage(error, 'Failed to delete skill')),
  });

  const sourceSkills = tab === 0 ? mineQuery.data || [] : publicQuery.data || [];
  const filteredSkills = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sourceSkills;
    return sourceSkills.filter((skill) =>
      [skill.name, skill.slug, skill.description, skill.category, ...(skill.tags || [])]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [search, sourceSkills]);

  const openCreate = () => {
    setEditingSkill(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (skill: AgentSkill) => {
    setEditingSkill(skill);
    setForm(formFromSkill(skill));
    setDialogOpen(true);
  };

  const saveSkill = () => {
    if (!form.name.trim() || (!editingSkill && !form.instructions.trim())) {
      toast.error('Name and instructions are required');
      return;
    }
    try {
      const payload: AgentSkillUpdate = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category.trim(),
        tags: csv(form.tags),
        is_active: form.isActive,
        is_public: form.isPublic,
      };
      const versionPayload = {
        instructions: form.instructions.trim(),
        trigger_description: form.triggerDescription.trim(),
        trigger_keywords: csv(form.triggerKeywords),
        required_tools: csv(form.requiredTools),
        optional_tools: csv(form.optionalTools),
        resources: jsonValue(form.resources, []),
        config_schema: jsonValue(form.configSchema, {}),
        tests: jsonValue(form.tests, []),
      };
      const original = editingSkill ? formFromSkill(editingSkill) : null;
      const versionChanged =
        !original || VERSION_FORM_FIELDS.some((field) => form[field] !== original[field]);
      if (versionChanged) Object.assign(payload, versionPayload);
      saveMutation.mutate({
        skill: editingSkill,
        payload: payload as AgentSkillCreate | AgentSkillUpdate,
      });
    } catch (error: any) {
      toast.error(`Invalid JSON: ${error?.message || 'check advanced fields'}`);
    }
  };

  const loading = tab === 0 ? mineQuery.isLoading : publicQuery.isLoading;
  const loadError = tab === 0 ? mineQuery.error : publicQuery.error;

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <SkillIcon color="primary" />
              <Typography variant="h4" fontWeight={800}>Skills</Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              Manage skills used by your agents, create your own, or start from a public template.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<PublicIcon />}
              onClick={() => setTab(1)}
            >
              Browse templates
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New blank skill
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_event, value) => setTab(value)}>
          <Tab label={`My skills (${mineQuery.data?.length || 0})`} />
          <Tab label={`Public templates (${publicQuery.data?.length || 0})`} />
        </Tabs>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tab === 0 ? 'Search my skill library' : 'Search public templates'}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
      ) : loadError ? (
        <Alert severity="error">Unable to load skills.</Alert>
      ) : filteredSkills.length === 0 ? (
        <Alert severity="info">
          {tab === 0
            ? 'No matching skills are assigned to your agents or created by you. Create a blank skill or start from a public template.'
            : 'No public templates are available. Confirm migrations 0056 and 0057 completed.'}
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {filteredSkills.map((skill) => (
            <Paper key={skill.id} variant="outlined" sx={{ p: 2.25 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={750} noWrap>{skill.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tab === 1 || !skill.can_edit ? `by ${skill.owner_name} · ` : ''}{skill.slug}
                    </Typography>
                  </Box>
                  {tab === 0 && skill.can_edit ? (
                    <Stack direction="row" spacing={0.25}>
                      <IconButton
                        size="small"
                        aria-label={`Edit ${skill.name}`}
                        onClick={() => openEdit(skill)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`Delete ${skill.name}`}
                        onClick={() => setDeleteSkill(skill)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ) : null}
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label={`v${skill.current_version?.version || '—'}`} />
                  {skill.category ? <Chip size="small" label={skill.category} /> : null}
                  {skill.is_public ? <Chip size="small" color="success" variant="outlined" label="Public" /> : null}
                  {skill.is_template ? <Chip size="small" color="info" variant="outlined" label="System template" /> : null}
                  {!skill.is_active ? <Chip size="small" label="Inactive" /> : null}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {skill.description || skill.current_version?.trigger_description || 'No description provided.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {skill.assignment_count} active assignment{skill.assignment_count === 1 ? '' : 's'}
                </Typography>
                {skill.can_clone && (tab === 1 || !skill.can_edit) ? (
                  <Button
                    variant="contained"
                    startIcon={<CloneIcon />}
                    onClick={() => cloneMutation.mutate(skill)}
                    disabled={cloneMutation.isPending}
                  >
                    {tab === 0 ? 'Create editable copy' : 'Create private copy'}
                  </Button>
                ) : null}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={750}>
                {editingSkill ? `Edit ${editingSkill.name}` : 'Create skill'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Instruction changes create a new immutable version.
              </Typography>
            </Box>
            <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField required fullWidth label="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              <TextField fullWidth label="Category" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} />
            </Stack>
            <TextField fullWidth multiline minRows={2} label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            <TextField fullWidth label="Tags" helperText="Comma-separated" value={form.tags} onChange={(event) => setForm({ ...form, tags: event.target.value })} />
            <TextField required fullWidth multiline minRows={8} label="Instructions" value={form.instructions} onChange={(event) => setForm({ ...form, instructions: event.target.value })} />
            <TextField fullWidth multiline minRows={2} label="Trigger description" value={form.triggerDescription} onChange={(event) => setForm({ ...form, triggerDescription: event.target.value })} />
            <TextField fullWidth label="Trigger keywords" helperText="Comma-separated" value={form.triggerKeywords} onChange={(event) => setForm({ ...form, triggerKeywords: event.target.value })} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label="Required tools" helperText="Comma-separated registry names" value={form.requiredTools} onChange={(event) => setForm({ ...form, requiredTools: event.target.value })} />
              <TextField fullWidth label="Optional tools" helperText="Comma-separated registry names" value={form.optionalTools} onChange={(event) => setForm({ ...form, optionalTools: event.target.value })} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth multiline minRows={5} label="Resources (JSON)" value={form.resources} onChange={(event) => setForm({ ...form, resources: event.target.value })} />
              <TextField fullWidth multiline minRows={5} label="Configuration schema (JSON)" value={form.configSchema} onChange={(event) => setForm({ ...form, configSchema: event.target.value })} />
              <TextField fullWidth multiline minRows={5} label="Tests (JSON)" value={form.tests} onChange={(event) => setForm({ ...form, tests: event.target.value })} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />} label="Active" />
              <FormControlLabel control={<Switch checked={form.isPublic} onChange={(event) => setForm({ ...form, isPublic: event.target.checked })} />} label="Share as public template" />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveSkill} disabled={saveMutation.isPending}>
            {editingSkill ? 'Save skill' : 'Create skill'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteSkill)} onClose={() => setDeleteSkill(null)}>
        <DialogTitle>Delete skill?</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteSkill ? `Delete “${deleteSkill.name}” and disable its assignments?` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSkill(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteSkill && deleteMutation.mutate(deleteSkill)} disabled={deleteMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SkillManagementPage;
